<template>
  <section class="n-view-login h-screen p-10 flex justify-center items-center">
    <NCard class="w-full max-w-[450px] shadow-md">
      <div class="text-center">
        <img class="mx-auto p-8" src="/images/logo-dark.png" alt="" />
      </div>
      <NForm ref="form" :model="model" :rules="rules" class="!grid-cols-1" @keydown.enter="onPost()">
        <NSelect
          v-model:value="model.providerType"
          :all-option="false"
          :show-search="false"
          :options="ptOptions"
          v-bind="providerTypeAttrs"
        />

        <NInputPreSuffix v-if="isMail" v-model:value="model.email" v-bind="emailAttrs">
          <template #suffix>
            <mail-outlined />
          </template>
        </NInputPreSuffix>

        <div v-if="isPhone" class="grid grid-cols-12 gap-1.5">
          <div class="col-start-1 col-end-6">
            <NSelect
              v-model:value="model.countryCode"
              :all-option="false"
              :show-search="false"
              :options="countryCodeOptions"
              v-bind="countryCodeTypeAttrs"
            />
          </div>
          <div class="col-start-6 col-end-13">
            <NInputPreSuffix v-if="isPhone" v-model:value="model.phone" v-bind="phoneAttrs">
              <template #suffix>
                <mobile-outlined />
              </template>
            </NInputPreSuffix>
          </div>
        </div>

        <NInputSafePassword v-model:value="model.password" v-bind="passwordAttrs" />

        <div class="flex space-x-2"></div>
        <NButton class="n-login-button" size="large" block type="primary" :loading="loading" @click="onPost()">
          <span>{{ $tl('login') }}</span>
        </NButton>
      </NForm>

      <div class="py-2 text-right pr-2">
        <span class="mr-5">
          <RouterLink to="/forgot">{{ $tl('forgotPassword') }}</RouterLink>
        </span>
      </div>
      <div class="text-center text-base">
        <span class="mr-2">{{ $tl('noAccount') }}</span>
        <span>
          <RouterLink to="/register" @click="onFbViewContent">{{ $tl('goRegister') }}</RouterLink>
        </span>
      </div>
      <div>
        <NDivider class="text-base">{{ $tl('loginQuick') }}</NDivider>
      </div>

      <div class="grid grid-cols-3 gap-1">
        <NButton class="!w-full !h-[32px] !flex !justify-center !items-center" @click="onGoogle()">
          <template #icon>
            <NImage :src="'/images/login-ex-google.svg'" :preview="false" :width="18" />
          </template>
        </NButton>
        <NButton class="!w-full !h-[32px] !flex !justify-center !items-center" @click="onFacebook()">
          <template #icon>
            <NImage :src="'/images/login-ex-facebook.svg'" :preview="false" :width="18" />
          </template>
        </NButton>
        <NButton class="!w-full !h-[32px] !flex !justify-center !items-center" @click="onApple()">
          <template #icon>
            <NImage :src="'/images/login-ex-apple.svg'" :preview="false" :width="18" class="!h-[20px]" />
          </template>
        </NButton>
      </div>

      <div class="mt-6 mb-3 text-center text-gray-600 grid items-center gap-3 grid-flow-col place-content-center">
        {{ COPYRIGHT }}
        <NChannelSwitch />
        <NLanguageSwitcher />
        <NHelper />
      </div>
    </NCard>
  </section>
</template>

<script setup>
import { domainEvent, useAppAction, useLanguage, useModelPost, useRouter, useValidate } from '@front/hooks';
import { DEFAULT_PORTAL } from '@/glue';
import { bootstrap, login } from '@front/api/utility';
import { computed, reactive, ref } from 'vue';
import { COPYRIGHT, VALIDATION_RULE } from '@front/constant';
import { getFrontURL, getMobiles } from '@/services';
import { generateMobiles } from "@front/utils";

const { router, routerTo } = useRouter();
const { setAction } = useAppAction();
const front = getFrontURL();
const { tl } = useLanguage();
const sourceMobileOptions = getMobiles();
const countryCodeOptions = computed(() => { return generateMobiles(sourceMobileOptions);});

const ptOptions = computed(() => [
  { label: tl('emailPassword'), value: 'MAIL_PASSWORD' },
  { label: tl('phonePassword'), value: 'PHONE_PASSWORD' },
]);

const MAIL_PASSWORD = 'MAIL_PASSWORD';
const PHONE_PASSWORD = 'PHONE_PASSWORD';
const isMail = computed(() => model.providerType === MAIL_PASSWORD);
const isPhone = computed(() => model.providerType === PHONE_PASSWORD);
const query = router.currentRoute.value.query;
const a = computed(() => query?.a ?? null);
const model = reactive({
  username: '',
  password: '',
  providerType: PHONE_PASSWORD,
  countryCode: '+55',
  email: undefined,
  phone: undefined,
});
const { form, loading, onPost } = useModelPost({ model, post: customLogin, successPath: DEFAULT_PORTAL });

const validation = computed(() => ({
  email: {
    required: model.providerType === MAIL_PASSWORD,
    isEmail: true,
    maxLength: VALIDATION_RULE.Customer.username.maxLength,
  },
  phone: {
    required: model.providerType === PHONE_PASSWORD,
    isMobile: true,
    maxLength: VALIDATION_RULE.Customer.mobile.maxLength,
  },
  countryCode: { required: model.providerType === PHONE_PASSWORD, maxlength: 5 },
  password: { required: true },
}));
const { rules } = useValidate({ formRef: form, validation });

const passwordAttrs = computed(() => ({
  size: 'large',
  name: 'password',
  placeholder: tl('loginPassword'),
  disabled: loading.value,
  type: 'password',
}));

const providerTypeAttrs = computed(() => ({
  size: 'large',
  name: 'providerType',
  placeholder: tl('providerType'),
  disabled: loading.value,
}));

const countryCodeTypeAttrs = computed(() => ({
  size: 'large',
  name: 'countryCode',
  placeholder: tl('countryCode'),
  disabled: loading.value,
}));

const phoneAttrs = computed(() => ({
  size: 'large',
  name: 'phone',
  placeholder: tl('cellphone'),
  disabled: loading.value,
}));

const emailAttrs = computed(() => ({
  size: 'large',
  name: 'email',
  placeholder: tl('loginUsername'),
  disabled: loading.value,
}));

bootstrap()
  .then(r => {
    routerTo(DEFAULT_PORTAL);
  })
  .catch(() => {});

const onGoogle = () => {
  setAction('LOGIN');
  const address = front + '/oauth2/authorization/google?action=QUICK_LOGIN&redirectUrl=' + front + '/%23/externalLoginVerify';
  window.open(address, '_self');
};

const onFacebook = () => {
  setAction('LOGIN');
  const address = front + '/oauth2/authorization/facebook?action=QUICK_LOGIN&redirectUrl=' + front + '/%23/externalLoginVerify';
  window.open(address, '_self');
};

const onApple = () => {
  setAction('LOGIN');
  AppleID.auth.signIn();
};

async function customLogin() {
  const params = {
    username: model.providerType === MAIL_PASSWORD ? model.email : model.countryCode + model.phone,
    password: model.password,
  };

  await login(params);
  domainEvent.dispatch('LOGIN');
}

function onFbViewContent() {
  window.fbq?.('track', 'ViewContent', {
    content_name: 'registration',
    contents: { channel: 'any', agent: a?.value ?? undefined },
  });
}
</script>

<style lang="less">
.n-view-login {
  background-image: url('/images/shape01.png');
  background-position: center;

  .n-language-switcher {
    gap: 6px;
  }

  .n-gutter-box-text {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
